import { apiFetch } from "@/apis/http/apiFetch";
import type { BondSectorCode } from "@/shared/types/bondSectors";
import type { RateStructureCode } from "@/shared/types/bondRateStructures";
import type { TaxStatusType } from "@/apis/common/enrichmentMapper";

/**
 * Backend `SourceOfRepaymentType` (Tower.SecurityMaster.Common.Enums). Note this
 * is a coarse GO/Revenue split, not the granular repayment-pledge taxonomy —
 * there is no separate field for the latter today.
 */
export type SourceOfRepaymentCode = "Unknown" | "Revenue" | "GeneralObligation" | "DoubleBarreled";

/** Backend `PaymentFrequencyType`. */
export type PaymentFrequencyCode =
  | "Unknown"
  | "Daily"
  | "Weekly"
  | "Monthly"
  | "Quarterly"
  | "SemiAnnual"
  | "Annual"
  | "AtMaturity"
  | "ZeroCoupon"
  | "SemiMonthly"
  | "NotApplicable";

/** Backend `RateType`, normalized from CGS field 47. */
export type RateTypeCode =
  | "Unknown"
  | "ContingentRate"
  | "FixedRate"
  | "FixedFloatingRate"
  | "VariableRate"
  | "StepDown"
  | "StepUp"
  | "IncreasingRate"
  | "PaymentInKind"
  | "SplitCoupon"
  | "ZeroCouponDiscounted";

/** Backend `CallMethodType` — valid on call-schedule entries only. */
export type CallMethodTypeCode = "Unknown" | "Cash" | "MakeWhole";

/** Backend `RedemptionMethod` — valid on put-schedule entries only. */
export type RedemptionMethodCode =
  | "Unknown"
  | "Optional"
  | "Mandatory"
  | "Extraordinary"
  | "Puttable";

/** Backend `OptionStyle`. */
export type OptionStyleCode = "Unknown" | "American" | "European" | "Bermudan";

/** Backend `ScheduleFrequency`. */
export type ScheduleFrequencyCode =
  | "Unknown"
  | "AnyTime"
  | "Annual"
  | "SemiAnnual"
  | "Quarterly"
  | "Monthly"
  | "Weekly"
  | "Daily"
  | "AtMaturity"
  | "Continuous"
  | "ByScheduled"
  | "OneTime"
  | "AnyInterestPaymentDate";

/** One redemption opportunity from S&P's `CallSchedule`. */
export interface CallScheduleEntry {
  startDate?: string;
  endDate?: string;
  price?: number;
  methodType: CallMethodTypeCode;
  optionStyle: OptionStyleCode;
  frequency: ScheduleFrequencyCode;
}

/** One put opportunity from S&P's `PutSchedule`. */
export interface PutScheduleEntry {
  startDate?: string;
  endDate?: string;
  price?: number;
  methodType: RedemptionMethodCode;
  optionStyle: OptionStyleCode;
  frequency: ScheduleFrequencyCode;
}

/** One mandatory sinking-fund redemption from S&P's `SinkSchedule`. */
export interface SinkScheduleEntry {
  startDate?: string;
  endDate?: string;
  price?: number;
  frequency: ScheduleFrequencyCode;
  type: string;
  amountAtMaturity?: number;
}

/**
 * A credit-enhancement provider. `type` is the raw S&P label (for example
 * "Letter of Credit", "Credit Insurance", "School Program", "Guarantor") — it is
 * not a normalized enum, so match on it case-insensitively.
 */
export interface CreditEnhancementEntry {
  provider: string;
  type: string;
  startDate?: string;
  endDate?: string;
}

/** Escrow composition for a defeased bond. */
export interface EscrowDetailEntry {
  escrowType: string;
  escrowPercentage?: number;
}

/** S&P's `InterestSchedule`, currently scoped to the raw ARS/VRDO code. */
export interface InterestScheduleEntry {
  arsVrdoCode: string;
}

/**
 * The published security-master read contract, mirroring
 * `Tower.SecurityMaster.ReadModel.Security` field-for-field. Returned by
 * `GET /api/v1/securities/{cusip}` and by the securities search endpoint.
 *
 * Enum-backed fields arrive as their backend enum-name string via
 * `JsonStringEnumConverter`. `null`/absent numbers and dates mean "not on
 * record" and must render as a dash rather than a zero or an epoch date.
 */
export interface Security {
  id: string;

  // Identity
  cusip: string;
  isin: string;
  fisn: string;
  tickerSymbol: string;

  // Issuer
  cusip6: string;
  issuerName: string;
  issuerAdl: string;
  lei: string;
  legalEntityName: string;
  domicile: string;
  stateCode: string;
  issuerType: string;
  issuerStatus: string;

  // Obligor
  obligorName: string;
  obligorCusip6: string;

  // Issue details
  issueDescription: string;
  issueAdditionalInfo: string;
  issueStatus: string;
  issueTypeCode: string;
  securityTypeDescription: string;
  issueGroup: string;
  currencyCode: string;

  // Debt type / classification
  securityCode: string;
  /** Derived Bond Type code; the "Debt type" display reads this, not the raw CGS description. */
  bondType: string;
  features: string[];
  isDoubleBarreled: boolean;
  sourceOfRepayment: SourceOfRepaymentCode;
  useOfProceeds: BondSectorCode | "Unknown";

  // Dates
  datedDate?: string;
  maturityDate?: string;
  nextCallDate?: string;
  nextPaymentDate?: string;
  firstCouponDate?: string;
  closingDate?: string;

  // Pricing / terms
  couponRate?: number;
  rateType: RateTypeCode;
  dayCountConvention: string;
  paymentFrequencyCode: PaymentFrequencyCode;
  /** Par amount of this CUSIP/maturity alone. */
  offeringAmount?: number;
  currentAmountOutstanding?: number;
  minDenomination?: number;
  issueDate?: string;
  denominationIncrease?: number;
  otherDenominations: string;
  issueYield?: number;
  /** Raw S&P string, not a derived bool — the real value set is unconfirmed. */
  isConvertibleOrExchangeable: string;
  interestSchedule: InterestScheduleEntry[];

  // Series / deal
  seriesNumber: string;
  seriesCode: string;
  /**
   * S&P's series size. Not the deal size: a deal can span several series, and
   * the deal-wide total lives on `DealInfo`, which this contract does not carry.
   */
  seriesSize?: number;

  // Call / redemption
  nextCallPrice?: number;
  hasExtraordinaryCall: boolean;
  callSchedule: CallScheduleEntry[];
  putSchedule: PutScheduleEntry[];
  sinkSchedule: SinkScheduleEntry[];

  // Enhancement / escrow / insurance
  insuredBy: string;
  enhancements: string;
  creditEnhancement: CreditEnhancementEntry[];
  escrowDetails: EscrowDetailEntry[];

  // Flags
  isBankQualified: boolean;
  isCallable: boolean;
  isPutable: boolean;
  isSinkable: boolean;
  isPreRefunded: boolean;
  isTaxable: boolean;
  taxStatus: TaxStatusType;
  isStateTaxApplicable: boolean;
  isAmt: boolean;
  isDtcEligible: boolean;
  isGreenBond: boolean;
  isFactorable: boolean;
  is144AQib: boolean;
  currentOutstandingFactor?: number;

  // ESG / thematic labelling — display only, deliberately not filterable.
  isEsg: boolean;
  esgLabel: string;
  greenBondType: string;
  greenBondVerifierCertifier: string;

  // Additional
  bondForm: string;
  saleType: string;

  rateStructure: RateStructureCode | "Other";
  /**
   * Raw backend `RedemptionStatus` enum name. Unlike the search filters, this is
   * not collapsed into the six-value UI taxonomy, so it can also be `Called`,
   * `Tendered`, and the rest of the full enum.
   */
  latestRedemptionType: string;

  lastUpdatedUtc: string;
}

/**
 * Fetch the full security-master record for a CUSIP. Returns `null` when the
 * security is not in the Gold index — an unknown CUSIP is an ordinary outcome
 * for a quote whose reference data has not been ingested yet, not an error.
 */
export async function fetchSecurityByCusip(
  cusip: string,
  signal?: AbortSignal,
): Promise<Security | null> {
  const res = await apiFetch(`/api/v1/securities/${encodeURIComponent(cusip)}`, {
    signal,
    allow404: true,
  });
  if (res.status === 404) return null;
  return (await res.json()) as Security;
}

/** One obligor autocomplete candidate returned by `/api/v1/securities/obligors`. */
interface ObligorSuggestion {
  /**
   * The obligor name exactly as stored in the security master. Passed back verbatim as an
   * `obligorName` search filter, so it must not be reformatted before use.
   */
  name: string;
  /** How many securities carry this obligor. Suggestions arrive ordered by this descending. */
  securityCount: number;
}

interface ObligorSuggestionsResponse {
  items: ObligorSuggestion[];
}

/** Matches `MaxObligorLimit` on the backend controller. */
export const MAX_OBLIGOR_SUGGESTIONS = 50;

/** Parameters of `GET /api/v1/securities` used by the security search clients. */
export interface SecuritySearchParams {
  /** Natural-language query, or a CUSIP fragment. */
  semanticQuery?: string;
  /** Exact CUSIP filter. */
  cusip?: string;
  limit: number;
}

/** `SecuritySearchResponse` on the backend. */
export interface SecuritySearchPage {
  items: Security[];
  totalCount: number;
  /** True for a vector search: `totalCount` is the candidate set, not the number of matches. */
  totalCountApproximate: boolean;
  /** True when the server stopped counting early: `totalCount` is a minimum, shown as "N+". */
  totalCountIsLowerBound: boolean;
}

export function securitySearchPath(search: Readonly<SecuritySearchParams>): string {
  const params = new URLSearchParams();
  if (search.cusip) params.set("cusip", search.cusip);
  if (search.semanticQuery) params.set("semanticQuery", search.semanticQuery);
  params.set("limit", String(search.limit));
  return `/api/v1/securities?${params.toString()}`;
}

interface SecuritySearchResponseBody {
  items?: Security[];
  totalCount?: number;
  totalCountApproximate?: boolean;
  totalCountIsLowerBound?: boolean;
}

export async function fetchSecuritySearch(
  search: Readonly<SecuritySearchParams>,
  signal?: AbortSignal,
): Promise<SecuritySearchPage> {
  const res = await apiFetch(securitySearchPath(search), { signal });
  const body = (await res.json()) as SecuritySearchResponseBody;
  return {
    items: body.items ?? [],
    totalCount: body.totalCount ?? 0,
    totalCountApproximate: body.totalCountApproximate ?? false,
    totalCountIsLowerBound: body.totalCountIsLowerBound ?? false,
  };
}

/**
 * Autocomplete obligor names. `query` is matched as a case-insensitive substring, so "airport"
 * finds "PORTLAND INTERNATIONAL AIRPORT". An empty query returns the most widely used obligors.
 */
export async function fetchObligorSuggestions(
  query: string,
  limit = 20,
  signal?: AbortSignal,
): Promise<ObligorSuggestion[]> {
  const params = new URLSearchParams();
  if (query) params.set("q", query);
  params.set("limit", String(Math.min(limit, MAX_OBLIGOR_SUGGESTIONS)));

  const res = await apiFetch(`/api/v1/securities/obligors?${params.toString()}`, { signal });
  const body = (await res.json()) as ObligorSuggestionsResponse;
  return body.items ?? [];
}
